import { useState, type FormEvent } from 'react'
import { useAuth, type TwoFactorChallenge } from '../hooks/useAuth.js'
import { api, ApiError } from '../lib/api.js'
import { Notice } from './ui.js'
import { ArrowRightIcon, BoltIcon, LockIcon } from './icons.js'

/**
 * The second half of signing in, shown once a password, phone code or Google
 * account has checked out and the account has 2FA on. Nothing is signed in
 * until this succeeds.
 */
export function TwoFactorStep({ challenge, onCancel }: { challenge: TwoFactorChallenge; onCancel: () => void }): React.ReactNode {
  const { verifyTwoFactor } = useAuth()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  const [resent, setResent] = useState(false)

  const expired = error?.is('2fa.expired') ?? false

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await verifyTwoFactor(challenge.token, code)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'unknown', 'Something went wrong.'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  async function resend(): Promise<void> {
    setError(null)
    try {
      await api('/v1/auth/2fa/resend', { method: 'POST', body: { token: challenge.token } })
      setResent(true)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'unknown', 'Could not resend the code.'))
    }
  }

  return (
    <div className="auth">
      <div className="auth__pane">
        <form className="auth__card" onSubmit={(event) => void submit(event)}>
          <p className="auth__brand">
            <BoltIcon size={24} style={{ color: 'var(--brand)' }} />
            <span>Outfalak</span> Checkout
          </p>

          <h1>Two-step verification</h1>
          <p className="auth__sub">
            {challenge.method === 'totp'
              ? 'Enter the 6-digit code from your authenticator app.'
              : `We sent a code on WhatsApp to the number ending ${challenge.phoneHint ?? '••••'}.`}
          </p>

          {error && <Notice kind="error">{error.message}</Notice>}
          {resent && !error && <Notice kind="info">A new code is on its way.</Notice>}

          {!expired && (
            <>
              <label className="field">
                <span>Code</span>
                <div className="input-group">
                  <LockIcon size={16} />
                  <input
                    type="text"
                    className="input"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={8}
                    required
                    autoFocus
                  />
                </div>
              </label>

              <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={busy || code.length < 4}>
                {busy ? 'Checking…' : 'Verify and sign in'}
                {!busy && <ArrowRightIcon size={16} />}
              </button>
            </>
          )}

          <div className="auth__row">
            <button type="button" className="btn btn--link" onClick={onCancel}>
              {expired ? 'Start again' : 'Use a different account'}
            </button>
            {challenge.method === 'whatsapp' && !expired && (
              <button type="button" className="btn btn--link" onClick={() => void resend()}>
                Resend code
              </button>
            )}
          </div>

          <p className="auth__sub" style={{ textAlign: 'center', fontSize: '.82rem' }}>
            Lost access to your phone? Contact support to have two-step verification reset.
          </p>
        </form>
      </div>
    </div>
  )
}
